import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, ArrowUpRight, CreditCard, Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtGhs = (n) => (n == null ? "—" : `GH₵ ${Number(n).toLocaleString()}`);

export default function Billing() {
  const [me, setMe] = useState(null);
  const [txs, setTxs] = useState([]);
  const [usage, setUsage] = useState(null);
  const [polling, setPolling] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const refresh = async () => {
    const [r1, r2, r3] = await Promise.all([
      api.get("/billing/me"),
      api.get("/billing/transactions"),
      api.get("/billing/usage").catch(() => ({ data: null })),
    ]);
    setMe(r1.data); setTxs(r2.data); setUsage(r3.data);
  };

  useEffect(() => { refresh(); }, []);

  // Paystack callback polling — checks ?reference= or ?trxref= in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("reference") || params.get("trxref");
    if (!ref) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/billing/checkout/status/${ref}`);
        const ps = r.data.payment_status;
        if (ps === "success") {
          toast.success("Payment successful — subscription activated.");
          setPolling(false); refresh();
          navigate("/billing", { replace: true });
          return;
        }
        if (["failed", "abandoned", "reversed"].includes(ps) || attempts >= 10) {
          toast.error(ps ? `Payment ${ps}.` : "Payment session timed out.");
          setPolling(false); return;
        }
        attempts += 1;
        setTimeout(poll, 2500);
      } catch {
        setPolling(false);
      }
    };
    poll();
  }, [location.search, navigate]);

  const cancel = async () => {
    if (!window.confirm("Cancel subscription? Your access remains until the period end.")) return;
    await api.post("/billing/cancel");
    toast.success("Subscription cancelled");
    refresh();
  };

  if (!me) return <div className="p-10 text-slate-400 font-mono">Loading…</div>;

  const gated = new URLSearchParams(location.search).get("gated") === "1";
  const org = me.organization;
  const plan = me.plan;
  const usagePct = org && org.ai_quota_monthly
    ? Math.min(100, (org.ai_usage_month / org.ai_quota_monthly) * 100)
    : 0;
  const seatPct = org && org.seats_limit
    ? Math.min(100, (org.seats_used / org.seats_limit) * 100)
    : 0;

  return (
    <div className="p-8 space-y-6 max-w-6xl mx-auto" data-testid="billing-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// Subscription & Licensing</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Billing</h1>
          <p className="text-slate-400 mt-2 text-sm">Your institutional license, AI usage, and invoices.</p>
        </div>
        <Link to="/pricing" className="px-4 py-2 flex items-center gap-2 text-sm font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>
          Compare plans <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {polling && (
        <div className="border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
          <span className="text-sm text-amber-300">Confirming payment with Paystack…</span>
        </div>
      )}

      {gated && (
        <div className="border border-red-500/30 bg-red-500/5 p-4 flex items-start gap-3" data-testid="gated-banner">
          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="text-sm text-red-300">Module not included in your current suite</div>
            <div className="text-xs text-slate-400 mt-1">
              That capability is part of a higher-tier institutional license. Upgrade your suite to unlock it.
            </div>
          </div>
          <Link to="/pricing" className="px-3 py-1.5 text-xs font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>Upgrade</Link>
        </div>
      )}

      {/* Current plan card */}
      {plan ? (
        <div className="nexus-card p-7" data-testid="current-plan">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 className="w-5 h-5" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
                <span className="label-eyebrow">{org.status} · {org.billing_cycle}</span>
              </div>
              <h2 className="font-display text-3xl text-white">{plan.name}</h2>
              <p className="text-sm text-slate-400 mt-1">{plan.audience}</p>
              {org.current_period_end && (
                <div className="font-mono text-xs text-slate-500 mt-3">
                  RENEWS {new Date(org.current_period_end).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="font-mono text-3xl text-white">
                {org.billing_cycle === "annual" ? `GH₵ ${plan.annual_ghs?.toLocaleString()}` : `GH₵ ${plan.monthly_ghs?.toLocaleString()}`}
              </div>
              <div className="font-mono text-[10px] text-slate-500 tracking-widest mt-1">
                {org.billing_cycle === "annual" ? "PER YEAR" : "PER MONTH"} · GHS
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/5">
            <div>
              <div className="label-eyebrow mb-2">Seats</div>
              <div className="flex justify-between text-sm">
                <span className="text-white font-mono">{org.seats_used} / {org.seats_limit}</span>
                <span className="text-slate-500 font-mono text-xs">{seatPct.toFixed(0)}%</span>
              </div>
              <div className="h-1 bg-[#10182B] mt-2"><div className="h-full" style={{ width: `${seatPct}%`, background: "var(--gold-500)" }}></div></div>
            </div>
            <div>
              <div className="label-eyebrow mb-2">AI generations / month</div>
              <div className="flex justify-between text-sm">
                <span className="text-white font-mono">{org.ai_usage_month} / {org.ai_quota_monthly}</span>
                <span className="text-slate-500 font-mono text-xs">{usagePct.toFixed(0)}%</span>
              </div>
              <div className="h-1 bg-[#10182B] mt-2"><div className="h-full" style={{ width: `${usagePct}%`, background: usagePct > 90 ? "#EF4444" : "var(--gold-500)" }}></div></div>
            </div>
            <div>
              <div className="label-eyebrow mb-2">Suite</div>
              <div className="text-white font-mono uppercase">{org.suite}</div>
              <div className="text-[10px] text-slate-500 font-mono mt-1">{me.modules.length} modules enabled</div>
            </div>
          </div>

          {org.status === "trial" && (
            <div className="mt-6 p-4 border border-amber-500/30 bg-amber-500/5 flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5" strokeWidth={1.5} />
              <div className="flex-1">
                <div className="text-sm text-amber-300">Trial active</div>
                <div className="text-xs text-slate-400 mt-1">
                  Your trial ends {new Date(org.trial_ends_at).toLocaleDateString()}. Convert to a paid plan to keep access.
                </div>
              </div>
              <Link to="/pricing" className="px-3 py-1.5 text-xs font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>Upgrade</Link>
            </div>
          )}

          {org.status === "active" && (
            <div className="mt-6 flex justify-end">
              <button onClick={cancel} className="px-4 py-2 text-xs font-mono tracking-widest text-slate-500 hover:text-red-400 border border-white/10 hover:border-red-500/30">
                CANCEL SUBSCRIPTION
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="nexus-card p-10 text-center">
          <CreditCard className="w-8 h-8 mx-auto text-slate-500 mb-4" strokeWidth={1} />
          <div className="font-display text-xl text-white">No active subscription</div>
          <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
            Choose an institutional license to unlock the full Pandora Nexus operating system.
          </p>
          <Link to="/pricing" className="inline-flex mt-6 px-5 py-2.5 items-center gap-2 font-medium text-sm" style={{ background: "var(--gold-500)", color: "#050B14" }}>
            View plans <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* Feature-limit quota bars */}
      {usage && !usage.internal && (
        <div className="nexus-card p-6" data-testid="usage-caps">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">// Resource Usage</div>
              <div className="text-white font-display text-lg mt-1">Plan limits · {usage.plan_name || "—"}</div>
            </div>
            <Link to="/pricing" className="text-xs font-mono tracking-widest text-slate-400 hover:text-white">RAISE LIMITS →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { key: "projects", label: "Projects", data: usage.projects },
              { key: "vault_documents", label: "Vault Docs", data: usage.vault_documents },
              { key: "marketplace_installs", label: "Marketplace", data: usage.marketplace_installs },
              { key: "storage", label: "Storage", data: { used: usage.storage?.used_gb, cap: usage.storage?.cap_gb }, unit: "GB" },
            ].map(({ key, label, data, unit }) => {
              const used = data?.used ?? 0;
              const cap = data?.cap ?? 0;
              const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
              const warn = pct >= 80;
              const danger = pct >= 95;
              return (
                <div key={key} data-testid={`cap-${key}`}>
                  <div className="label-eyebrow mb-2">{label}</div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white font-mono">
                      {used}{unit ? ` ${unit}` : ""} <span className="text-slate-500">/ {cap === 0 ? "∞" : `${cap}${unit ? ` ${unit}` : ""}`}</span>
                    </span>
                    <span className={`font-mono text-xs ${danger ? "text-red-400" : warn ? "text-amber-400" : "text-slate-500"}`}>
                      {cap === 0 ? "" : `${pct.toFixed(0)}%`}
                    </span>
                  </div>
                  <div className="h-1 bg-[#10182B] mt-2">
                    <div className="h-full" style={{
                      width: `${pct}%`,
                      background: danger ? "#EF4444" : warn ? "#F59E0B" : "var(--gold-500)",
                    }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Internal banner */}
      {me.is_internal && (
        <div className="nexus-card p-5 border-[#D4AF37]/30">
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 font-mono text-[10px] tracking-widest" style={{ background: "var(--gold-500)", color: "#050B14" }}>INTERNAL</span>
            <div className="text-sm text-slate-300">
              You are a Pandora Management Consult Ltd internal user — full access across all suites.
            </div>
          </div>
        </div>
      )}

      {/* Transactions */}
      <div>
        <div className="label-eyebrow mb-3">// Invoice History ({txs.length})</div>
        <div className="nexus-card overflow-hidden">
          {txs.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No transactions yet.</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  {["Date", "Plan", "Cycle", "Amount", "Status"].map((h) => (
                    <th key={h} className="px-5 py-3 text-left label-eyebrow">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {txs.map((t) => (
                  <tr key={t.id} className="border-b border-white/5">
                    <td className="px-5 py-3 text-xs font-mono text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="px-5 py-3 text-sm text-white">{t.plan_id?.replace(/_/g, " ")}</td>
                    <td className="px-5 py-3 text-xs font-mono text-slate-400 uppercase">{t.billing_cycle}</td>
                    <td className="px-5 py-3 font-mono text-sm text-white">{fmtGhs(t.amount)}</td>
                    <td className="px-5 py-3">
                      <span className="font-mono text-[10px] uppercase tracking-widest"
                        style={{ color: t.payment_status === "success" || t.payment_status === "success_activated" ? "#22C55E" : t.payment_status === "initiated" ? "#F59E0B" : "#94A3B8" }}>
                        {t.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
